import { collection, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import { db, storage } from "../firebase-key";
import { useAuth } from "../state/appContext";

const documentSections = {
  personal: ["PHOTO", "AADHAAR", "PAN", "RESUME", "ADDRESS PROOF"],
  company: ["ID CARD", "EMPLOYMENT LETTER", "NDA FORM"],
};

const TeamDocuments = () => {
  const { user } = useAuth();
  const [docs, setDocs] = useState({});
  const [uploading, setUploading] = useState(false);

  const loadDocs = useCallback(async () => {
    if (!user?.uid) return;

    const refPath = collection(db, "teams", user.uid, "documents");
    const snap = await getDocs(refPath);

    const d = {};
    snap.forEach((doc) => (d[doc.id] = doc.data()));
    setDocs(d);
  }, [user?.uid]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const uploadDoc = async (section, name, file) => {
    if (!file) return;

    setUploading(true);

    const storageRef = ref(
      storage,
      `team_documents/${user.uid}/${section}/${name}.pdf`
    );

    try {
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await setDoc(
        doc(db, "teams", user.uid, "documents", `${section}__${name}`),
        {
          section,
          name,
          url,
          uploaded_at: new Date(),
        }
      );

      toast.success(`${name} uploaded ✔`);
      loadDocs();
    } catch (err) {
      console.log(err);
      toast.error("Upload failed ❌");
    }

    setUploading(false);
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-md border">
      <h2 className="text-2xl font-semibold mb-4">📄 Your Documents</h2>

      {Object.entries(documentSections).map(([section, items]) => (
        <div key={section} className="mb-10">
          <h3 className="text-xl font-bold mb-3 capitalize">
            {section.replace("_", " ")}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((docName) => {
              const key = `${section}__${docName}`;
              const existing = docs[key];

              return (
                <div key={docName} className="p-4 border rounded-md bg-gray-50">
                  <p className="font-semibold">{docName}</p>

                  <input
                    type="file"
                    accept="application/pdf"
                    disabled={uploading}
                    className="mt-2 text-sm"
                    onChange={(e) =>
                      uploadDoc(section, docName, e.target.files[0])
                    }
                  />

                  {existing?.url && (
                    <a
                      href={existing.url}
                      target="_blank"
                      className="text-blue-600 underline mt-2 block text-sm"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const Team = () => {
  const { user, loading } = useAuth();
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("info");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (user) reset(user);
  }, [user, reset]);

  const onSubmit = async (data) => {
    setSaving(true);

    try {
      await updateDoc(doc(db, "teams", user.uid), {
        ...data,
        updated_at: new Date(),
      });

      toast.success("Team profile updated! 🎉");
    } catch (err) {
      console.log(err);
      toast.error("Update failed ❌");
    }

    setSaving(false);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-700 text-lg">
        Loading team profile...
      </div>
    );
  }

  return (
    <div className="min-h-100 py-28">
      <ToastContainer />

      {/* TABS */}
      <div className="w-full flex justify-center mb-8">
        <div className="flex bg-gray-100 p-1 rounded-xl shadow-sm">
          <button
            onClick={() => setTab("info")}
            className={`px-6 py-2 rounded-lg text-sm ${
              tab === "info"
                ? "bg-white shadow text-blue-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Team Info
          </button>

          <button
            onClick={() => setTab("docs")}
            className={`px-6 py-2 rounded-lg text-sm ${
              tab === "docs"
                ? "bg-white shadow text-blue-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            Documents
          </button>
        </div>
      </div>

      {tab === "info" && (
        <div className="flex justify-center items-center bg-[#f7f9fc] p-4">
          <div className="w-full max-w-4xl bg-white p-10 rounded-2xl shadow-lg border">

            <h1 className="text-3xl font-semibold text-center text-gray-800 mb-6">
              Edit Team Profile ✏️
            </h1>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5"
            >
              <InputField label="Name" name="name" register={register} errors={errors} />

              <InputField label="Address" name="address" register={register} errors={errors} />

              <InputField
                label="PAN"
                name="pan"
                register={register}
                errors={errors}
                validate={{ pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/ }}
              />

              <InputField
                label="Aadhaar"
                name="aadhaar"
                register={register}
                errors={errors}
                validate={{ pattern: /^\d{12}$/ }}
              />

              <InputField label="Age" name="age" register={register} errors={errors} validate={{ pattern: /^\d+$/ }} />

              <InputField label="Gender" name="gender" register={register} errors={errors} />

              {/* Email cannot be edited */}
              <div className="col-span-3">
                <label className="text-sm text-gray-700">Email (not editable)</label>
                <input
                  disabled
                  value={user.email}
                  className="w-full mt-1 px-4 py-3 bg-gray-200 border rounded-lg text-gray-600 cursor-not-allowed"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="col-span-3 w-full py-3 mt-4 rounded-lg bg-blue-600 text-white font-semibold"
              >
                {saving ? "Saving…" : "Update Profile"}
              </button>
            </form>
          </div>
        </div>
      )}

      {tab === "docs" && <TeamDocuments />}
    </div>
  );
};

const InputField = ({ label, name, register, errors, type = "text", validate }) => (
  <div>
    <label className="text-sm text-gray-700">{label}</label>
    <input
      type={type}
      {...register(name, {
        required: `${label} is required`,
        ...(validate?.pattern && {
          pattern: { value: validate.pattern, message: `Invalid ${label}` },
        }),
      })}
      className="w-full mt-1 px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 outline-none"
      placeholder={label}
    />
    {errors[name] && <p className="text-red-500 text-sm">{errors[name].message}</p>}
  </div>
);

export default Team;
